import { useTranslation } from 'react-i18next';

export interface HourCount {
  readonly weekday?: string;
  readonly hour?: number;
  readonly count?: number;
}

/** The Egyptian working week starts on Saturday (ASM-004). */
const WEEK = ['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'] as const;

/** A known Saturday, for asking Intl for each weekday's name. 2024-01-06 was one. */
const A_SATURDAY = Date.UTC(2024, 0, 6);

/** Five steps of the sequential ramp, palest first, after the empty track. */
const STEPS = 5;

/**
 * When the clinic is busy, as a weekday-by-hour grid (CR-33).
 *
 * The question is staffing — which evenings need a second doctor, which mornings could close — and
 * it is a question about a pattern across the week, which a grid answers at a glance and a list of
 * 168 numbers does not. One hue, light to dark, because the job is magnitude; five steps rather
 * than a continuous blend, because a reader can tell five steps apart and cannot tell 37% from 41%.
 * Exact figures are on hover and focus, and in each cell's accessible name.
 *
 * Only the hours that have anything in them are drawn, widened to whole working hours on each
 * side, so a clinic open four to ten is not drawn as a mostly empty day.
 */
export function WeekHeatmap({ hours, label }: { readonly hours: readonly HourCount[]; readonly label: string }) {
  const { t, i18n } = useTranslation();

  const counts = new Map<string, number>();
  let first = 23;
  let last = 0;
  let largest = 0;
  for (const cell of hours) {
    if (!cell.weekday || cell.hour === undefined || !cell.count) {
      continue;
    }
    counts.set(`${cell.weekday}:${cell.hour}`, cell.count);
    first = Math.min(first, cell.hour);
    last = Math.max(last, cell.hour);
    largest = Math.max(largest, cell.count);
  }
  if (largest === 0) {
    return <p className="muted">{t('overview.busiestEmpty')}</p>;
  }

  const span: number[] = [];
  for (let hour = first; hour <= last; hour++) {
    span.push(hour);
  }
  const dayName = (index: number, style: 'short' | 'long') =>
    new Intl.DateTimeFormat(i18n.language, { weekday: style, timeZone: 'UTC' }).format(
      new Date(A_SATURDAY + index * 86_400_000),
    );
  const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;
  // Every hour labelled while there is room; every other one once the day is long.
  const labelEvery = span.length > 12 ? 2 : 1;

  return (
    <figure className="heatmap">
      <div
        className="heatmap__grid"
        role="table"
        aria-label={label}
        style={{ gridTemplateColumns: `auto repeat(${span.length}, minmax(0, 1fr))` }}
      >
        <span aria-hidden="true" />
        {span.map((hour, index) => (
          <span key={`h${hour}`} className="heatmap__hour" aria-hidden="true">
            {index % labelEvery === 0 ? String(hour).padStart(2, '0') : ''}
          </span>
        ))}
        {WEEK.map((weekday, dayIndex) => (
          <div key={weekday} className="heatmap__row" role="row">
            <span className="heatmap__day" role="rowheader">
              {dayName(dayIndex, 'short')}
            </span>
            {span.map((hour) => {
              const value = counts.get(`${weekday}:${hour}`) ?? 0;
              const step = value === 0 ? 0 : Math.max(1, Math.ceil((value / largest) * STEPS));
              const description = t('overview.busiestCell', {
                day: dayName(dayIndex, 'long'),
                hour: hourLabel(hour),
                count: value,
              });
              return (
                <span
                  key={hour}
                  className={`heatmap__cell heatmap__cell--${step} has-tip`}
                  role="cell"
                  tabIndex={value > 0 ? 0 : -1}
                  aria-label={description}
                >
                  <span className="chart-tip" role="presentation">
                    {description}
                  </span>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="heatmap__legend" aria-hidden="true">
        <span>{t('overview.fewer')}</span>
        {Array.from({ length: STEPS + 1 }, (_, step) => (
          <span key={step} className={`heatmap__cell heatmap__cell--${step}`} />
        ))}
        <span>{t('overview.more')}</span>
      </div>
    </figure>
  );
}
